import { Fragment, useCallback } from "react";

import clsx from "clsx";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useOverviewContext } from "@features/overview-v2/hooks";
import { type OverviewFilters } from "@features/overview-v2/types";
import { CompareCustomDateRangeSelect } from "@features/sales-v3/top-level-filters/components/compare-date-range-menu/compare-custom-date-range-select";
import { SamePeriodDifferentYearSubmenu } from "@features/sales-v3/top-level-filters/components/compare-date-range-menu/same-period-different-year-submenu";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { constants } from "@north-beam/nb-common";
import { renderChevronAnimation } from "@shared/selects/utils";

export const CompareDateRangeMenu = (props: { className?: string }) => {
  const prettyLogEventPage = usePrettyLogEventPage();

  const {
    filters: {
      dateRange,
      attributionModel,
      attributionWindow,
      accountingMode,
      timeGranularity,
      dateRangeInterval,
      compareDateRange,
      level,
    },
    setCompareDateRange: _setCompareDateRange,
  } = useOverviewContext();

  const pageContext = {
    dateRange,
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRangeInterval,
    compareDateRange,
    level,
  };

  const setCompareDateRange = useCallback(
    (value: OverviewFilters["compareDateRange"] | null) => {
      prettyLogEventPage({
        domain: "Filters Bar",
        action: "onChange",
        additionalActionInfo: "Set Compare Date Range",
        data: {
          newCompareDateRange: value,
          pageContext,
        },
      });
      _setCompareDateRange(value);
    },
    [_setCompareDateRange, prettyLogEventPage],
  );

  return (
    <Popover className="relative text-sm">
      <PopoverButton
        as="div"
        className={clsx("nb-input-container w-40", props.className)}
      >
        {({ open }) => (
          <div
            className={clsx(
              "nb-input flex justify-between items-center bg-v2-primary border-v2-secondary text-v2-primary cursor-pointer h-inherit",
              { "border border-cosmic ring-1 ring-cosmic": open },
            )}
          >
            <div
              className={clsx(
                "text-ellipsis whitespace-nowrap overflow-hidden",
                "max-w-full flex-grow text-left",
              )}
            >
              {!compareDateRange && (
                <span className="text-disabled">Compare to...</span>
              )}
              {compareDateRange &&
                constants.CompareDateRangeTypes.getLabel(
                  compareDateRange.value,
                )}
            </div>
            <span
              className="pl-2 justify-self-end"
              onClick={(event) => {
                if (!open) {
                  event.preventDefault();
                  event.stopPropagation();
                }
                setCompareDateRange(null);
              }}
            >
              {compareDateRange ? (
                <i
                  className={clsx(
                    "fa-regular fa-sharp fa-circle-xmark text-sm",
                    "group-hover:text-cosmic+1 text-cosmic",
                  )}
                />
              ) : (
                renderChevronAnimation(open)
              )}
            </span>
          </div>
        )}
      </PopoverButton>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <PopoverPanel className="absolute rounded-md bg-v2-primary elevation-1 mt-2 py-1 w-56 z-10">
          {({ close }) => (
            <>
              <div
                className={clsx(
                  "hover:cursor-pointer hover:bg-cosmic-5 hover:font-semibold px-4 py-1.5",
                  "flex items-center justify-between",
                )}
                onClick={() => {
                  setCompareDateRange({
                    value: "previousPeriod",
                  });
                  close();
                }}
              >
                <span className="text-v2-primary leading-loose">
                  Previous period
                </span>
              </div>
              {(timeGranularity === "weekly" ||
                timeGranularity === "monthly") && (
                <div
                  className={clsx(
                    "hover:cursor-pointer hover:bg-cosmic-5 hover:font-semibold px-4 py-1.5",
                    "flex items-center justify-between",
                  )}
                  onClick={() => {
                    setCompareDateRange({
                      value: constants.CompareDateRangeTypes.LASTPERIOD,
                    });
                    close();
                  }}
                >
                  <span className="text-v2-primary leading-loose">
                    {constants.CompareDateRangeTypes.getLabel("lastPeriod")}
                  </span>
                </div>
              )}
              <div
                className={clsx(
                  "hover:cursor-pointer hover:bg-cosmic-5 hover:font-semibold px-4 py-1.5",
                  "flex items-center justify-between",
                )}
                onClick={() => {
                  setCompareDateRange({
                    value: constants.CompareDateRangeTypes.SAMEPERIODPRIORYEAR,
                  });
                  close();
                }}
              >
                <span className="text-v2-primary leading-loose">
                  Same period, prior year
                </span>
              </div>
              <SamePeriodDifferentYearSubmenu
                selectSamePeriodDifferentYear={(
                  calendar: "standard",
                  year: number,
                ) => {
                  setCompareDateRange({
                    value: "samePeriodDifferentYear",
                    year,
                    calendar,
                  });
                  close();
                }}
                compareDateRange={compareDateRange}
                placement="left-start"
              />
              <CompareCustomDateRangeSelect
                timeGranularity={timeGranularity}
                compareDateRange={compareDateRange}
                placement="bottom"
                selectCustomDateRange={({ startDate, endDate }) => {
                  setCompareDateRange({
                    value: "custom",
                    startDate,
                    endDate,
                  });
                  close();
                }}
              />
            </>
          )}
        </PopoverPanel>
      </Transition>
    </Popover>
  );
};
